import { CalendarIcon, ClockIcon, ExternalLinkIcon, PhotographIcon, SparklesIcon, TagIcon, ViewGridIcon } from "@heroicons/react/outline";
import { EyeIcon, EyeOffIcon, HashtagIcon, PencilIcon } from "@heroicons/react/solid";
import { HttpError, useOne, useParse } from "@refinedev/core";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { DescriptionItem } from "components/descriptionItem";
import { FullScreenLoading } from "components/fullScreenLoading";
import { HistoryLodModal } from "components/historyLogModal";
import { IFipixEventDetail, IOrgIdsItem } from "interfaces";
import moment from "moment";
import { useState } from "react";
import { Link } from "react-router-dom";

import CONFIG from "app-config/config.json";
import { isHaveActionPermission } from "utills/helpers";

export const FipixEventDetail: React.FC = () => {
  const [openLogModal, setOpenLogModal] = useState(false);

  const parse = useParse();
  const { params } = parse();
  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<IFipixEventDetail>({
    resource: "fipix/events",
    id: params?.id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!params?.id,
    } as any,
  });

  const fipixEventDetailData = data?.data ?? null;
  const { data: orgIds } = useOne<IOrgIdsItem[], HttpError>({
    resource: "organization/ids",
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!fipixEventDetailData?.organization_id,
    },
  });

  const organizationName =
    (orgIds?.data ?? []).find(
      (org: IOrgIdsItem) => org.id === fipixEventDetailData?.organization_id
    )?.name_en || fipixEventDetailData?.organization_id || "";

  if (isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="w-full flex flex-col items-center gap-y-3 sm:gap-y-4">
      <Breadcrumbs
        customPageLabel="Fipix Event Detail"
        additionalLabel={
          <div className="flex gap-x-2.5">
            <Button
              className="bg-white border-brand-500 border-[1px] border-solid text-brand-500 hover:bg-white active:bg-slate-200"
              onClick={() => setOpenLogModal(true)}
            >
              <span className="sr-only">History Log</span>
              <ClockIcon className="w-4 h-4 text-brand-500" />{" "}
              <span className="hidden sm:block sm:ml-2">Log</span>
            </Button>
            {isHaveActionPermission("events", "fipix", "edit") ? (
              <Link to={`/fipix/events/${fipixEventDetailData?.id}/edit`}>
                <Button>
                  <span className="sr-only">Edit</span>
                  <PencilIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">Edit</span>
                </Button>
              </Link>
            ) : (
              <></>
            )}
          </div>
        }
      />
      <div className="box-container p-0">
        <div className="divide-y divide-gray-100">
          <DescriptionItem
            icon={TagIcon}
            label="Status"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {fipixEventDetailData?.is_active ? "Active" : "Inactive"}
              </p>
            }
          />
          <DescriptionItem
            icon={SparklesIcon}
            label="Event name eng"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {fipixEventDetailData?.name_en}
              </p>
            }
          />
          <DescriptionItem
            icon={SparklesIcon}
            label="Event name vie"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {fipixEventDetailData?.name_vi}
              </p>
            }
          />
          <DescriptionItem
            icon={CalendarIcon}
            label="Event date"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {fipixEventDetailData?.start_date ? moment(fipixEventDetailData?.start_date).format("DD/MM/YYYY") : ""}
              </p>
            }
          />
          <DescriptionItem
            icon={ViewGridIcon}
            label="Organization"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {organizationName}
              </p>
            }
          />
          <DescriptionItem
            icon={ExternalLinkIcon}
            label="Event link"
            content={
              <Link to={`${CONFIG.FIPIX_DOMAIN}/vi/${fipixEventDetailData?.slug}`} target="_blank" className="block w-fit hover:underline text-brand-500">
                <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                  {`${CONFIG.FIPIX_DOMAIN}/vi/${fipixEventDetailData?.slug}`}
                </p>
              </Link>
            }
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Banner"
            content={
              fipixEventDetailData?.banner_url ? (
                <img
                  className="w-auto max-w-full max-h-40 bg-gray-200"
                  src={fipixEventDetailData?.banner_url}
                  alt="Blog Banner"
                />
              ) : (
                <></>
              )
            }
          />
          <DescriptionItem
            icon={EyeIcon}
            label="Face recognition"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {fipixEventDetailData?.has_face_recognition ? "Yes" : "No"}
              </p>
            }
          />
          <DescriptionItem
            icon={HashtagIcon}
            label="BIB recognition"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {fipixEventDetailData?.has_bib_recognition ? "Yes" : "No"}
              </p>
            }
          />
          <DescriptionItem
            icon={EyeOffIcon}
            label="Hidden"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {fipixEventDetailData?.hidden ? "Yes" : "No"}
              </p>
            }
          />
          <DescriptionItem
            icon={PhotographIcon}
            label="Show all images"
            content={
              <p className="flex gap-x-2 text-sm leading-6 sm:col-span-4">
                {fipixEventDetailData?.show_all_images ? "Yes" : "No"}
              </p>
            }
          />
        </div>
      </div>
      <HistoryLodModal
        resource={`fipix/events/${params?.id}/history`}
        open={openLogModal}
        onClose={() => setOpenLogModal(false)}
        key={"sub-category-log"}
      />
      {(isFetching && isPreviousData) ? <FullScreenLoading /> : <></>}
    </div>
  );
}
